import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { FieldLabel } from './ui/Field'
import { api, extractApiError } from '../lib/api'
import { spaceListResponse } from '../lib/types'

export function FolderMoveDialog({ open, folder, workspaceId, onClose }) {
  const qc = useQueryClient()
  const [targetSpaceId, setTargetSpaceId] = useState(null)

  const spacesQuery = useQuery({
    queryKey: ['spaces', workspaceId],
    enabled: open && Boolean(workspaceId),
    queryFn: async () => {
      if (!workspaceId) return { data: [] }
      const res = await api.get(`/workspaces/${workspaceId}/spaces`)
      return spaceListResponse.parse(res.data)
    },
  })
  const spaces = (spacesQuery.data?.data ?? []).filter((s) => !s.isArchived)
  const otherSpaces = folder ? spaces.filter((s) => s.id !== folder.spaceId) : spaces

  useEffect(() => {
    if (open) {
      setTargetSpaceId(otherSpaces[0]?.id ?? null)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, folder?.id])

  const moveMutation = useMutation({
    mutationFn: async () => {
      if (!folder) throw new Error('No folder selected.')
      if (!targetSpaceId) throw new Error('Pick a destination Space.')
      await api.patch(`/spaces/${folder.spaceId}/folders/${folder.id}`, {
        spaceId: targetSpaceId,
      })
      return { fromSpaceId: folder.spaceId, toSpaceId: targetSpaceId }
    },
    onSuccess: ({ fromSpaceId, toSpaceId }) => {
      toast.success('Project moved successfully.')
      onClose()
      void qc.invalidateQueries({ queryKey: ['folders', fromSpaceId] })
      void qc.invalidateQueries({ queryKey: ['folders', toSpaceId] })
    },
    onError: (err) => toast.error(extractApiError(err).message),
  })

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Move To"
      description={folder ? `Move "${folder.name}" to a different Space.` : ''}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={moveMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => moveMutation.mutate()}
            loading={moveMutation.isPending}
            disabled={!targetSpaceId || otherSpaces.length === 0}
          >
            Move
          </Button>
        </>
      }
    >
      <div>
        <FieldLabel label="Destination Space" htmlFor="folder-move-space" />
        <select
          id="folder-move-space"
          value={targetSpaceId ?? ''}
          onChange={(e) => setTargetSpaceId(e.target.value || null)}
          disabled={spacesQuery.isLoading || otherSpaces.length === 0}
          className="w-full h-10 px-3 rounded-md border border-[color:var(--color-rule)] bg-[color:var(--color-card)] text-[color:var(--color-ink)] outline-none focus:border-[color:var(--color-accent)] cursor-pointer disabled:opacity-60"
        >
          <option value="" disabled hidden>
            {spacesQuery.isLoading
              ? 'Loading spaces…'
              : otherSpaces.length === 0
              ? 'No other Spaces available'
              : 'Pick a Space'}
          </option>
          {otherSpaces.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    </Modal>
  )
}
